/**
 * User Tracking and Metadata Collection Utility
 * Compliant with DPDP Act 2023
 * Captures user login, IP, location, and session metadata
 */

export interface UserMetadata {
  sessionId: string;
  timestamp: string;
  userAgent: string;
  language: string;
  screenResolution: string;
  timezone: string;
  referrer: string;
  ipAddress?: string;
  location?: {
    city?: string;
    region?: string;
    country?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface UserLoginData {
  userId?: string;
  loginTimestamp: string;
  loginMethod: string;
  deviceInfo: {
    platform: string;
    browser: string;
    isMobile: boolean;
  };
  metadata: UserMetadata;
}

/**
 * Generate a unique session ID
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Collect basic user metadata from browser
 */
export const collectUserMetadata = (): UserMetadata => {
  if (typeof window === 'undefined') {
    return {
      sessionId: generateSessionId(),
      timestamp: new Date().toISOString(),
      userAgent: 'server-side-render',
      language: 'en',
      screenResolution: 'unknown',
      timezone: 'UTC',
      referrer: 'none',
    };
  }

  return {
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    language: navigator.language || 'en',
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || 'direct',
  };
};

/**
 * Get user's IP address (requires backend API)
 * This should be called from server-side or through a secure API endpoint
 */
export const getUserIPAddress = async (): Promise<string | null> => {
  try {
    // Using a public IP API service (replace with your own secure backend endpoint)
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || null;
  } catch (error) {
    console.error('Failed to fetch IP address:', error);
    return null;
  }
};

/**
 * Get user's approximate location based on IP
 * This should be called from server-side or through a secure API endpoint
 * Note: For production, use a proper geolocation service with user consent
 */
export const getUserLocation = async (ipAddress: string): Promise<UserMetadata['location'] | null> => {
  try {
    // Using a public geolocation API (replace with your own secure backend endpoint)
    const response = await fetch(`https://ipapi.co/${ipAddress}/json/`);
    const data = await response.json();
    
    return {
      city: data.city,
      region: data.region,
      country: data.country_name,
      coordinates: {
        latitude: data.latitude,
        longitude: data.longitude,
      },
    };
  } catch (error) {
    console.error('Failed to fetch location:', error);
    return null;
  }
};

/**
 * Track user login with full metadata
 */
export const trackUserLogin = async (
  userId?: string,
  loginMethod: string = 'email'
): Promise<UserLoginData> => {
  const metadata = collectUserMetadata();
  
  // Get IP address
  const ipAddress = await getUserIPAddress();
  if (ipAddress) {
    metadata.ipAddress = ipAddress;
    
    // Get location based on IP
    const location = await getUserLocation(ipAddress);
    if (location) {
      metadata.location = location;
    }
  }

  const loginData: UserLoginData = {
    userId,
    loginTimestamp: new Date().toISOString(),
    loginMethod,
    deviceInfo: {
      platform: navigator.platform || 'unknown',
      browser: detectBrowser(),
      isMobile: /Mobile|Android|iPhone/i.test(navigator.userAgent),
    },
    metadata,
  };

  // Store in localStorage (encrypted in production)
  try {
    const existingSessions = JSON.parse(
      localStorage.getItem('gencarbon_user_sessions') || '[]'
    );
    existingSessions.push(loginData);
    
    // Keep only last 10 sessions
    if (existingSessions.length > 10) {
      existingSessions.shift();
    }
    
    localStorage.setItem('gencarbon_user_sessions', JSON.stringify(existingSessions));
  } catch (error) {
    console.error('Failed to store session data:', error);
  }

  // Log for compliance audit trail
  console.log('User Login Tracked (DPDP Compliant):', {
    timestamp: loginData.loginTimestamp,
    method: loginData.loginMethod,
    hasConsent: hasUserConsent(),
  });

  return loginData;
};

/**
 * Detect browser name
 */
const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  
  return 'Unknown';
};

/**
 * Check if user has given consent for tracking
 */
export const hasUserConsent = (): boolean => {
  try {
    const consent = localStorage.getItem('gencarbon_cookie_consent');
    if (!consent) return false;
    
    const consentData = JSON.parse(consent);
    return consentData.accepted && consentData.analytics;
  } catch {
    return false;
  }
};

/**
 * Get all user sessions (for user data access request under DPDP)
 */
export const getUserSessions = (): UserLoginData[] => {
  try {
    return JSON.parse(localStorage.getItem('gencarbon_user_sessions') || '[]');
  } catch {
    return [];
  }
};

/**
 * Delete all user data (for user data erasure request under DPDP)
 */
export const deleteUserData = (): void => {
  localStorage.removeItem('gencarbon_user_sessions');
  localStorage.removeItem('gencarbon_cookie_consent');
  console.log('User data deleted per DPDP erasure request');
};

/**
 * Export user data (for data portability request under DPDP)
 */
export const exportUserData = (): string => {
  const sessions = getUserSessions();
  const consent = localStorage.getItem('gencarbon_cookie_consent');
  
  const exportData = {
    sessions,
    consent: consent ? JSON.parse(consent) : null,
    exportedAt: new Date().toISOString(),
  };
  
  return JSON.stringify(exportData, null, 2);
};